import React from 'react';
import { ROUTE_PATHS } from 'shared/constants/routes';

export type EntityType = 'project' | 'client' | 'member' | 'description';

/**
 * Builds a URL to the reports page pre-filtered to show the Detailed tab
 * for the given entity. Opening this in a new tab gives a drill-down view.
 */
function resolveHref(type: EntityType, id: string, startDate?: string, endDate?: string): string | null {
  const dateParams = [
    startDate ? `start=${encodeURIComponent(startDate)}` : '',
    endDate ? `end=${encodeURIComponent(endDate)}` : '',
  ].filter(Boolean).join('&');

  const withDates = (base: string) => dateParams ? `${base}&${dateParams}` : base;

  switch (type) {
    case 'project':
      return withDates(`${ROUTE_PATHS.REPORTS}?tab=detailed&projectIds=${encodeURIComponent(id)}`);
    case 'client':
      return withDates(`${ROUTE_PATHS.REPORTS}?tab=detailed&clientIds=${encodeURIComponent(id)}`);
    case 'member':
      return withDates(`${ROUTE_PATHS.REPORTS}?tab=detailed&userIds=${encodeURIComponent(id)}`);
    case 'description':
    default:
      return null;
  }
}

interface EntityLinkProps {
  type: EntityType;
  id?: string | null;
  children: React.ReactNode;
  className?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Renders an <a> that opens a pre-filtered reports drill-down view in a new tab
 * on right-click → "Open link in new tab". Left-click also navigates to that URL.
 * No visual change — inherits all parent styles, no underline, no color change.
 *
 * Falls back to <span> when there is no resolvable href (e.g. description type or no id).
 */
const EntityLink: React.FC<EntityLinkProps> = ({ type, id, children, className, startDate, endDate }) => {
  const href = id ? resolveHref(type, id, startDate, endDate) : null;

  if (!href) {
    return <span className={className}>{children}</span>;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={(e) => e.preventDefault()}
      style={{
        color: 'inherit',
        textDecoration: 'none',
        cursor: 'default',
      }}
    >
      {children}
    </a>
  );
};

export default EntityLink;
